import { Lock } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { Link } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { photoSrcSet } from '@/lib/albums'
import { dealColumns, isPrivate, momentPhotoUrl, paragraphs, written, type MomentSummary } from '@/lib/moments'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'
import { isLive, shapeOf } from '../../../shared/momentStyle'
import { ToldLabel } from './Told'

// A wall of moments, each drawn the way its author chose: a photo whole, a few words set large on paper, or a story
// with its opening lines. Columns like a pinboard, so photos keep their own shape and nothing is cropped. They're dealt
// into the columns in turn, so the newest read across the top row rather than down the first column.
// `tight` is the Moments page's Cards: on a phone, two columns 4px from the screen's edges and 4px apart, as the photo
// wall is.
export function MomentWall({ moments, tight }: { moments: MomentSummary[]; tight?: boolean }) {
  const columns = dealColumns(moments, useColumnCount())
  return (
    <div
      role="list"
      aria-label="Moments"
      className={cn('flex items-start sm:gap-3', tight ? 'max-sm:-mx-4 max-sm:gap-1 sm:-mx-1' : '-mx-1 gap-2')}
    >
      {columns.map((column, c) => (
        <div key={c} role="presentation" className="flex min-w-0 flex-1 flex-col gap-3 sm:gap-4">
          {column.map(({ item: moment, index }) => (
            <div key={moment.id} role="listitem" className={tight ? 'sm:px-1' : 'px-1'}>
              <MomentPin moment={moment} index={index} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const columnQuery = (width: number) => `(min-width: ${width}px)`
const subscribeColumns = (changed: () => void) => {
  const queries = [640, 1024].map((w) => matchMedia(columnQuery(w)))
  for (const q of queries) q.addEventListener('change', changed)
  return () => queries.forEach((q) => q.removeEventListener('change', changed))
}
// Two columns on a phone, three on a tablet, four on a computer.
const useColumnCount = () =>
  useSyncExternalStore(subscribeColumns, () =>
    matchMedia(columnQuery(1024)).matches ? 4 : matchMedia(columnQuery(640)).matches ? 3 : 2,
  )

const card = 'relative flex flex-col gap-1.5 rounded-2xl outline-none'
// A link over the whole tile, so anywhere on it opens the moment and screen readers hear one name.
const cover =
  'after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-[3px] focus-visible:after:ring-ring/50'

export function MomentPin({ moment, index = 0 }: { moment: MomentSummary; index?: number }) {
  const shape = shapeOf(moment)
  const to = `/moment/${moment.id}`
  const by = (
    <span className="flex min-w-0 items-center gap-1.5 px-1 text-xs text-muted-foreground">
      <Avatar person={moment.author} className="size-[1.125rem] text-[0.55rem] shadow-none" />
      <span className="truncate">{moment.author.name}</span>
      {isPrivate(moment) && <Lock className="size-3 shrink-0" aria-label="Only you" />}
    </span>
  )
  // When it was added, and the year it's from when its author gave one. A Now moment that's still live keeps its dot.
  const year = moment.tense !== 'now' ? moment.happenedOn?.slice(0, 4) : undefined
  const when = isLive(moment) ? (
    <ToldLabel moment={moment} />
  ) : (
    <span className="px-1 text-xs text-muted-foreground">
      Added {written(moment.sharedAt ?? moment.createdAt)}
      {year && ` · From ${year}`}
    </span>
  )
  if (shape === 'words') {
    const words = moment.body.trim() || moment.title
    return (
      <article className={card}>
        <div className={cn('flex flex-col gap-3 rounded-2xl bg-secondary px-3.5 pt-4 pb-3.5', 'moment-press')}>
          <span aria-hidden className="bg-logo h-[3px] w-6 rounded-full" />
          <Link to={to} className={cn('text-[1.05rem] leading-snug font-medium text-pretty', cover)}>
            <span className="sr-only">{moment.title}: </span>
            <span className="line-clamp-[9]">{words}</span>
          </Link>
          <span className="-mx-1 flex flex-col gap-1">
            {when}
            {by}
          </span>
        </div>
      </article>
    )
  }
  const photo = moment.photos[0]
  // A story with no photo sits on paper of its own, so it reads as one card among the photos rather than loose text.
  if (!photo)
    return (
      <article className={card}>
        <div className="moment-press flex flex-col gap-2 rounded-2xl bg-secondary px-3.5 pt-3.5 pb-3">
          <Link to={to} className={cn('text-[0.95rem] leading-snug font-semibold text-balance', cover)}>
            {moment.title}
          </Link>
          {moment.body && (
            <p className="line-clamp-5 text-xs leading-snug text-muted-foreground">{paragraphs(moment.body)[0]}</p>
          )}
          <span className="-mx-1 flex flex-col gap-1">
            {when}
            {by}
          </span>
        </div>
      </article>
    )
  return (
    <article className={card}>
      <div
        className={cn('moment-press relative overflow-hidden rounded-2xl', tint(index))}
        style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
      >
        <img
          src={momentPhotoUrl(photo.id, 'thumb')}
          srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['thumb', 'medium'], momentPhotoUrl)}
          sizes="(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
        {isLive(moment) && (
          <ToldLabel moment={moment} onPhoto className="absolute top-2 left-2 max-w-[calc(100%-1rem)]" />
        )}
        {moment.photoCount > 1 && (
          <span className="absolute right-2 bottom-2 rounded-full bg-viewer/60 px-2 py-0.5 text-xs font-semibold text-viewer-foreground">
            1 / {moment.photoCount}
          </span>
        )}
      </div>
      <Link to={to} className={cn('px-1 text-sm leading-tight font-semibold text-balance', cover)}>
        {moment.title}
      </Link>
      {shape === 'story' && moment.body && (
        <p className="line-clamp-3 px-1 text-xs leading-snug text-muted-foreground">{paragraphs(moment.body)[0]}</p>
      )}
      {!isLive(moment) && when}
      {by}
    </article>
  )
}
